import { Badge } from "@/components/ui/badge";
import { LEVEL_BADGE_CLASSES } from "@/lib/level-badge";
import { cn } from "@/lib/utils";
import { combineVulnerability } from "@shared/scoring/vulnerability";
import type { Score } from "@shared/scoring/types";

const SCORES: Score[] = [1, 2, 3, 4];

/** Every distinct pair, order-independent — combineVulnerability is symmetric,
 * so (1,2) and (2,1) would be duplicate rows. */
const PAIRS: Array<[Score, Score]> = SCORES.flatMap((damage) =>
  SCORES.filter((disruption) => disruption >= damage).map((disruption) => [damage, disruption] as [Score, Score]),
);

/**
 * Generated by calling combineVulnerability, not transcribed, so the page
 * cannot disagree with the engine. The step-up rows are highlighted because
 * they are the only ones the plain "higher of the two" wording doesn't
 * predict.
 */
export function VulnerabilityRuleTable() {
  return (
    <div className="overflow-x-auto p-5">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-border text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
            <th className="pb-2">Damage to asset</th>
            <th className="pb-2 text-center">Disruption to activity</th>
            <th className="pb-2 text-right">Vulnerability</th>
          </tr>
        </thead>
        <tbody>
          {PAIRS.map(([damage, disruption]) => {
            const result = combineVulnerability(damage, disruption);
            const steppedUp = damage === disruption && (damage === 2 || damage === 3);
            return (
              <tr
                key={`${damage}-${disruption}`}
                className={cn("border-t border-border/60", steppedUp && "bg-amber-50/60")}
              >
                <td className={cn("py-3", steppedUp && "font-bold")}>{damage}</td>
                <td className={cn("py-3 text-center", steppedUp && "font-bold")}>{disruption}</td>
                <td className="py-3 text-right">
                  <Badge className={LEVEL_BADGE_CLASSES[result]}>{result}</Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Highlighted rows are the step-up cases: when both scores are equal at 2 or 3, vulnerability rises one
        level rather than staying flat.
      </p>
    </div>
  );
}
